import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/data";
import { articleImages } from "@/mocks/images";
import SEOHead from "@/components/base/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到 - kaiyun.con 官方导航"
          description="未找到该文章，请浏览其他内容。"
          keywords="kaiyun.con,开云平台"
          canonicalUrl="https://kaiyun.con/article"
        />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20 pb-12">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-emotion-sad-line text-2xl text-secondary-500"></i>
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
              <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-sm w-4 h-4 flex items-center justify-center"></i>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = articles
    .filter(
      (a) =>
        a.categorySlug === article.categorySlug && a.slug !== article.slug
    )
    .slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    url: `https://kaiyun.con/article/${article.slug}`,
    description: article.excerpt,
    datePublished: article.publishedAt,
    author: {
      "@type": "Organization",
      name: "kaiyun.con 官方导航",
    },
    publisher: {
      "@type": "Organization",
      name: "kaiyun.con 官方导航",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://kaiyun.con/article/${article.slug}`,
    },
    keywords: article.tags.join(","),
  };

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: article.category, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - kaiyun.con 官方导航`}
        description={article.excerpt}
        keywords={`kaiyun.con,${article.tags.join(",")},开云平台`}
        canonicalUrl={`https://kaiyun.con/article/${article.slug}`}
        ogType="article"
        articlePublishedTime={article.publishedAt}
        articleModifiedTime={article.publishedAt}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <article className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="max-w-[860px]">
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors duration-200 whitespace-nowrap"
                  >
                    {article.category}
                  </Link>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-0.5 rounded-full text-xs bg-secondary-100 text-secondary-600 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-snug">
                  {article.title}
                </h1>

                <div className="flex items-center gap-4 text-xs text-foreground-500">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-line text-xs"></i>
                    </div>
                    <span>发布时间：{article.publishedAt}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line text-xs"></i>
                    </div>
                    <span>阅读时间：{article.readTime}</span>
                  </div>
                </div>
              </div>

              <div className="h-64 md:h-80 rounded-lg overflow-hidden mb-8">
                <img
                  src={articleImages[article.imageSeq] || articleImages["art-nav-01"]}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose-content text-sm md:text-base text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-5">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={articleImages[ra.imageSeq] || articleImages["art-nav-01"]}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                          {ra.title}
                        </h3>
                        <span className="text-xs text-foreground-400 mt-1 inline-block">
                          {ra.publishedAt}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 text-center">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-sm w-4 h-4 flex items-center justify-center"></i>
                返回 {article.category} 列表
              </Link>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}